<template>
  <div class="p-4 md:p-6 lg:p-8 xl:p-10 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="mb-6 md:mb-8">
      <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">
        Panel de Contabilidad
      </h1>
      <p class="text-sm md:text-base text-slate-600 dark:text-slate-400">{{ fechaActual }}</p>
    </div>

    <!-- Filtro mes/año -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm mb-4 flex flex-wrap gap-3 items-end">
      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Mes</label>
        <select v-model="filtro.mes" @change="cargarStats" class="form-control form-control-sm">
          <option v-for="m in meses" :key="m.val" :value="m.val">{{ m.label }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Año</label>
        <select v-model="filtro.anio" @change="cargarStats" class="form-control form-control-sm">
          <option v-for="a in anios" :key="a" :value="a">{{ a }}</option>
        </select>
      </div>
      <div class="text-xs text-slate-500 dark:text-slate-400 self-center">
        Mostrando datos de <strong>{{ meses.find(m => m.val === filtro.mes)?.label }} {{ filtro.anio }}</strong>
      </div>
    </div>

    <!-- Acciones rápidas -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6 md:mb-8">
      <h2 class="text-base font-semibold text-slate-700 dark:text-slate-300 mb-3">Acciones Rápidas</h2>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
        <button @click="$router.push('/contabilidad/ingresos-diarios')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border-0">
          <i class="bx bx-book-open text-2xl md:text-3xl text-custom-primary mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Libro Servicios</span>
        </button>
        <button @click="$router.push('/contabilidad/liquidacion-tarjetas')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border-0">
          <i class="bx bx-credit-card text-2xl md:text-3xl text-custom-primary mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Liquidación Tarjetas</span>
        </button>
        <button @click="$router.push('/cierre-caja-fechas')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border-0">
          <i class="bx bx-calculator text-2xl md:text-3xl text-custom-primary mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Cierre de Caja</span>
        </button>
        <button @click="$router.push('/ingresos-hospedaje')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border-0">
          <i class="bx bx-hotel text-2xl md:text-3xl text-custom-primary mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Ingresos Hotel</span>
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <i class="bx bx-loader-alt bx-spin text-4xl text-custom-primary"></i>
    </div>

    <template v-else>
      <!-- KPIs del sistema (registros_contables — pagos reales procesados) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">

        <!-- Ingresos del día -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between mb-3">
            <div class="p-2 md:p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <i class="bx bx-dollar text-2xl md:text-3xl text-green-600 dark:text-green-400"></i>
            </div>
            <span class="text-xs font-medium text-green-600 dark:text-green-400">Hoy</span>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1 font-mono">
            Q{{ fmt(stats.ingresos_dia) }}
          </h3>
          <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Ingresos del Día</p>
          <div class="mt-2 flex gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span>Hotel: Q{{ fmt(stats.ingresos_hotel_dia) }}</span>
            <span>Rest.: Q{{ fmt(stats.ingresos_restaurante_dia) }}</span>
          </div>
        </div>

        <!-- Ingresos del mes -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between mb-3">
            <div class="p-2 md:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <i class="bx bx-bar-chart-alt-2 text-2xl md:text-3xl text-blue-600 dark:text-blue-400"></i>
            </div>
            <span class="text-xs font-medium text-blue-600 dark:text-blue-400">Mes actual</span>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1 font-mono">
            Q{{ fmt(stats.ingresos_mes) }}
          </h3>
          <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Ingresos del Mes</p>
          <div class="mt-1 flex gap-2 text-xs text-slate-400 dark:text-slate-500 flex-wrap">
            <span>Hotel: Q{{ fmt(stats.ingresos_hotel_mes) }}</span>
            <span>Rest.: Q{{ fmt(stats.ingresos_restaurante_mes) }}</span>
          </div>
        </div>

        <!-- IVA acumulado -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between mb-3">
            <div class="p-2 md:p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <i class="bx bx-receipt text-2xl md:text-3xl text-yellow-600 dark:text-yellow-400"></i>
            </div>
            <span class="text-xs font-medium text-yellow-600 dark:text-yellow-400">IVA 12%</span>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1 font-mono">
            Q{{ fmt(stats.iva_mes) }}
          </h3>
          <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">IVA Acumulado Mes</p>
        </div>

        <!-- Liquidaciones con diferencia -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
          <div class="flex items-center justify-between mb-3">
            <div class="p-2 md:p-3 rounded-lg"
              :class="stats.liquidaciones_pendientes > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-slate-100 dark:bg-slate-700'">
              <i class="bx bx-credit-card text-2xl md:text-3xl"
                :class="stats.liquidaciones_pendientes > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'"></i>
            </div>
            <span class="text-xs font-medium"
              :class="stats.liquidaciones_pendientes > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'">
              {{ stats.liquidaciones_pendientes > 0 ? 'Con diferencia' : 'Al día' }}
            </span>
          </div>
          <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
            {{ stats.liquidaciones_pendientes }}
          </h3>
          <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Liquidaciones con diferencia</p>
        </div>
      </div>

      <!-- Gráfica + Resumen libro del mes -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">

        <!-- Gráfica ingresos últimos 7 días (sistema) -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm">
          <h2 class="text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            Ingresos Últimos 7 Días
          </h2>
          <div class="h-48 md:h-64 flex items-end justify-between gap-2">
            <div v-for="(dia, i) in stats.ingresos_semana" :key="i" class="flex-1 flex flex-col items-center">
              <span class="text-xs text-slate-500 dark:text-slate-400 mb-1">
                {{ dia.monto > 0 ? 'Q' + fmtShort(dia.monto) : '' }}
              </span>
              <div
                class="w-full rounded-t-lg transition-all hover:opacity-80"
                :class="dia.monto > 0 ? 'bg-custom-primary' : 'bg-slate-200 dark:bg-slate-700'"
                :style="{ height: `${maxIngreso > 0 ? Math.max((dia.monto / maxIngreso) * 100, dia.monto > 0 ? 5 : 2) : 2}%` }"
              ></div>
              <span class="text-xs mt-2 text-slate-600 dark:text-slate-400 text-center">{{ dia.dia }}</span>
            </div>
          </div>
        </div>

        <!-- Resumen Libro de Servicios del mes -->
        <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm">
          <h2 class="text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
            Libro de Servicios — Mes Actual
          </h2>

          <div v-if="stats.libro_mes.registros === 0"
            class="flex flex-col items-center justify-center py-8 text-slate-400 dark:text-slate-500">
            <i class="bx bx-book text-4xl mb-2"></i>
            <p class="text-sm text-center">No hay registros en el libro este mes.</p>
            <p class="text-xs text-center mt-1">Ingresa los datos diarios en el Libro de Servicios.</p>
            <button @click="$router.push('/contabilidad/ingresos-diarios')"
              class="mt-3 px-4 py-2 rounded-lg text-xs font-medium text-white border-0"
              style="background:#B39659;">
              + Agregar registro
            </button>
          </div>

          <div v-else class="space-y-3">
            <div class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-custom-primary"></div>
                <span class="text-sm text-slate-600 dark:text-slate-400">Total Ventas</span>
              </div>
              <span class="font-semibold font-mono text-slate-800 dark:text-slate-100">Q{{ fmt(stats.libro_mes.total) }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-green-500"></div>
                <span class="text-sm text-slate-600 dark:text-slate-400">Efectivo</span>
              </div>
              <span class="font-mono text-green-600 dark:text-green-400">Q{{ fmt(stats.libro_mes.efectivo) }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-blue-500"></div>
                <span class="text-sm text-slate-600 dark:text-slate-400">Tarjeta</span>
              </div>
              <span class="font-mono text-blue-600 dark:text-blue-400">Q{{ fmt(stats.libro_mes.tarjeta) }}</span>
            </div>
            <div class="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-700">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-purple-500"></div>
                <span class="text-sm text-slate-600 dark:text-slate-400">Transferencias</span>
              </div>
              <span class="font-mono text-purple-600 dark:text-purple-400">Q{{ fmt(stats.libro_mes.transferencias) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Total a Depositar</span>
              </div>
              <span class="font-mono font-bold text-custom-primary">Q{{ fmt(stats.libro_mes.total_depositar) }}</span>
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button @click="$router.push('/contabilidad/ingresos-diarios')"
              class="text-custom-primary hover:opacity-80 text-sm font-medium transition-opacity border-0 bg-transparent">
              Ver libro completo →
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';

const defaultStats = () => ({
  ingresos_dia: 0,
  ingresos_hotel_dia: 0,
  ingresos_restaurante_dia: 0,
  ingresos_mes: 0,
  ingresos_hotel_mes: 0,
  ingresos_restaurante_mes: 0,
  ingresos_servicios_mes: 0,
  iva_mes: 0,
  liquidaciones_pendientes: 0,
  ingresos_semana: Array.from({ length: 7 }, () => ({ dia: '...', fecha: '', monto: 0 })),
  libro_mes: { registros: 0, total: 0, efectivo: 0, tarjeta: 0, transferencias: 0, total_depositar: 0, depositado: 0 },
});

const hoy = new Date();

export default {
  name: 'DashboardContador',
  data() {
    return {
      loading: true,
      stats: defaultStats(),
      filtro: {
        mes:  hoy.getMonth() + 1,
        anio: hoy.getFullYear(),
      },
      meses: [
        { val: 1, label: 'Enero' },   { val: 2, label: 'Febrero' },  { val: 3, label: 'Marzo' },
        { val: 4, label: 'Abril' },   { val: 5, label: 'Mayo' },     { val: 6, label: 'Junio' },
        { val: 7, label: 'Julio' },   { val: 8, label: 'Agosto' },   { val: 9, label: 'Septiembre' },
        { val: 10, label: 'Octubre' },{ val: 11, label: 'Noviembre' },{ val: 12, label: 'Diciembre' },
      ],
      anios: Array.from({ length: 4 }, (_, i) => hoy.getFullYear() - i),
    };
  },
  computed: {
    fechaActual() {
      return new Date().toLocaleDateString('es-ES', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });
    },
    maxIngreso() {
      return Math.max(...this.stats.ingresos_semana.map(d => d.monto), 1);
    },
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    fmtShort(num) {
      if (!num || num === 0) return '0';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
      return Number(num).toFixed(0);
    },
    async cargarStats() {
      try {
        this.loading = true;
        const { data } = await axios.get(`${API_URL}/contabilidad/dashboard`, {
          params: { mes: this.filtro.mes, anio: this.filtro.anio },
        });
        this.stats = { ...defaultStats(), ...data };
      } catch (e) {
        console.error('Error cargando dashboard contabilidad:', e);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.cargarStats();
  },
};
</script>
